/**
 * bimGateParts — the two driveway GATES' parametric geometry, ported from the
 * house-ifc Python modules (`devices/sliding-gate/sliding_gate.py`,
 * `devices/swing-gate/swing_gate.py`), exactly as `bimGarageDoorParts.ts` ports
 * the garage door. Every number below is a module constant, mm → m.
 *
 * ── The frame (same conversion as the door) ─────────────────────────────────
 *
 * The MODULES' frame: origin = the gate's OUTER (street) face, mid-width, at
 * ground level; `+X` across the width; `+Y` INTO the property; `+Z` up.
 *
 * The DEVICE-MODEL frame the renderer places on the widget's pin: `+X` across
 * the width, `+Y` UP, `+Z` OUT of the plane, origin at the CENTRE of the closed
 * gate (mid-width, mid-height, on the gate's plane) — so
 *
 *   · `y_dev = z_py − fullHeight/2`
 *   · `z_dev = −PIN_STANDOFF − y_py`   (the pin sits 20 mm proud; the gate's own
 *     plane is therefore at −20 mm and every module depth is measured inward:
 *     the street leaf at [−70, −20] mm, the inner leaf behind it, the posts and
 *     the ground rail inside those).
 *
 * ── The live state ──────────────────────────────────────────────────────────
 *
 * The app's cover plumbing feeds a device's `position` (0 = closed … 100 = open
 * for an ACCESS device — see `bimDeviceModel.deviceModelState`) into the model as
 * a *drop*: how much product hangs. For a gate that reads as the fraction still
 * CLOSED, and both gates derive their motion from it:
 *
 *   · the SLIDING gate slides its leaves along their telescopic travel
 *     (`travel_i = (i+1)·W/2 + (1−i)·overlap` — the front leaf half the opening,
 *     the rear one the whole of it, so both stow over ONE leaf-width of frontage);
 *   · the SWING gate turns each leaf about its own hinge axis by up to 95°, into
 *     the property, and every part of a turning leaf carries its own `yawDeg`.
 */

import type { Vec3 } from './bimShadeSpec';

/** Which shared material a gate part uses (the modules' own style keys). */
export type GatePartMaterial = 'leaf' | 'frame' | 'steel' | 'hardware';

export interface GatePartBox {
  /** Stable slot key across states. */
  key: string;
  material: GatePartMaterial;
  /** Box centre, metres, in the DEVICE-MODEL frame above. */
  center: Vec3;
  /** Full box size, metres: [X = width, Y = up, Z = out]. */
  size: Vec3;
  /** Swing gates: the leaf's turn about the vertical axis, degrees. */
  yawDeg?: number;
}

const MM = 0.001;

/** `devices/sliding-gate/sliding_gate.py` — telescopic sliding gate. */
export const SLIDING_GATE = {
  sections: 2,
  overlap: 150 * MM,
  clear: 20 * MM,
  leafT: 50 * MM,
  bar: 30 * MM,
  barPitch: 130 * MM,
  barInset: 60 * MM,
  railTopH: 60 * MM,
  railBotH: 80 * MM,
  post: 120 * MM,
  postLift: 90 * MM,
  groundRailW: 90 * MM,
  groundRailH: 30 * MM,
  carriageW: 120 * MM,
  carriageD: 70 * MM,
  motorW: 210 * MM,
  motorH: 320 * MM,
  motorD: 170 * MM,
  pinStandoff: 20 * MM,
  minSections: 2,
  maxSections: 3,
} as const;

/** `devices/swing-gate/swing_gate.py` — two-leaf swing gate. */
export const SWING_GATE = {
  leaves: 2,
  midGap: 20 * MM,
  leafT: 55 * MM,
  stileW: 60 * MM,
  railH: 60 * MM,
  railBotH: 90 * MM,
  midRailZ: 0.62,
  bar: 25 * MM,
  barPitch: 130 * MM,
  post: 120 * MM,
  postLift: 80 * MM,
  hingeW: 90 * MM,
  hingeH: 60 * MM,
  hingeD: 45 * MM,
  hingeTopDrop: 260 * MM,
  hingeBotLift: 260 * MM,
  swingMaxDeg: 95,
  controllerW: 200 * MM,
  controllerH: 300 * MM,
  controllerD: 140 * MM,
  pinStandoff: 20 * MM,
  minLeaves: 1,
  maxLeaves: 2,
} as const;

/** The sections/leaves a family may be asked for, clamped into its own range. */
export function gateSections(sections: number | undefined): number {
  const n = typeof sections === 'number' && Number.isFinite(sections) ? Math.round(sections) : SLIDING_GATE.sections;
  return Math.min(SLIDING_GATE.maxSections, Math.max(SLIDING_GATE.minSections, n));
}

export function gateLeaves(leaves: number | undefined): number {
  const n = typeof leaves === 'number' && Number.isFinite(leaves) ? Math.round(leaves) : SWING_GATE.leaves;
  return n >= 2 ? 2 : 1;
}

export interface GateParams {
  widthM: number;
  /** How much of the gate still hangs closed (the cover plumbing's drop). */
  dropM: number;
  /** The frame's own full height — the opening, not the state. */
  dropFullM: number;
  prefs: { sections?: number; leaves?: number };
}

/** The open fraction a drop means: 0 = closed, 1 = fully open. */
function openFraction(dropM: number, dropFullM: number): number {
  const full = Math.max(0.3, dropFullM);
  return Math.min(1, Math.max(0, 1 - Math.min(full, Math.max(0, dropM)) / full));
}

/**
 * The telescopic sliding gate's parts at one size and state.
 *
 * Closed, the sections sit side by side over the opening with the OVERLAP
 * doubled at the centre; opening slides every leaf left along its own travel
 * (the front one the least), one track behind the other, so the stowed pair
 * occupies a single leaf-width of frontage.
 */
export function slidingGateParts(params: GateParams): GatePartBox[] {
  const w = Math.max(1, params.widthM);
  const h = Math.max(0.6, params.dropFullM);
  const p = openFraction(params.dropM, params.dropFullM);
  const sections = gateSections(params.prefs.sections);
  const half = w / 2;
  const leafW = half + SLIDING_GATE.overlap;
  const out: GatePartBox[] = [];

  /** One module box → one device-frame part. */
  const box = (
    key: string,
    material: GatePartMaterial,
    x0: number,
    y0: number,
    z0: number,
    dx: number,
    dy: number,
    dz: number,
  ): void => {
    if (dx <= 0 || dy <= 0 || dz <= 0) return;
    out.push({
      key,
      material,
      center: [x0 + dx / 2, z0 + dz / 2 - h / 2, -SLIDING_GATE.pinStandoff - y0 - dy / 2],
      size: [dx, dz, dy],
    });
  };

  for (let i = 0; i < sections; i += 1) {
    const travel = (i + 1) * half + (1 - i) * SLIDING_GATE.overlap;
    const y0 = i * (SLIDING_GATE.leafT + SLIDING_GATE.clear);
    const left = i * (half - SLIDING_GATE.overlap) - w / 2;
    const shift = -travel * p;
    const usable = leafW - 2 * SLIDING_GATE.barInset;
    const bars = Math.max(2, Math.round(usable / SLIDING_GATE.barPitch) + 1);
    const pitch = usable / (bars - 1);

    box(
      `leaf-${i}-rail-top`,
      'leaf',
      left + shift,
      y0,
      h - SLIDING_GATE.railTopH,
      leafW,
      SLIDING_GATE.leafT,
      SLIDING_GATE.railTopH,
    );
    box(
      `leaf-${i}-rail-bottom`,
      'leaf',
      left + shift,
      y0,
      SLIDING_GATE.groundRailH,
      leafW,
      SLIDING_GATE.leafT,
      SLIDING_GATE.railBotH,
    );
    for (let k = 0; k < bars; k += 1) {
      box(
        `leaf-${i}-bar-${k}`,
        'leaf',
        left + shift + SLIDING_GATE.barInset + k * pitch - SLIDING_GATE.bar / 2,
        y0 + (SLIDING_GATE.leafT - SLIDING_GATE.bar) / 2,
        SLIDING_GATE.groundRailH + SLIDING_GATE.railBotH,
        SLIDING_GATE.bar,
        SLIDING_GATE.bar,
        h - SLIDING_GATE.railTopH - SLIDING_GATE.railBotH - SLIDING_GATE.groundRailH,
      );
    }
    // the carriages this leaf rides (they travel with it)
    [left + shift + SLIDING_GATE.carriageW * 0.5, left + shift + leafW - 1.5 * SLIDING_GATE.carriageW].forEach(
      (cx, c) => {
        box(
          `leaf-${i}-carriage-${c}`,
          'steel',
          cx,
          y0 + (SLIDING_GATE.leafT - SLIDING_GATE.carriageD) / 2,
          0,
          SLIDING_GATE.carriageW,
          SLIDING_GATE.carriageD,
          SLIDING_GATE.groundRailH,
        );
      },
    );
  }

  // ── the fixed frame: two posts, the ground rail, the parking-side stop ────
  [-1, 1].forEach((sx) => {
    const x0 = sx < 0 ? -w / 2 - SLIDING_GATE.post : w / 2;
    box(
      sx < 0 ? 'post-l' : 'post-r',
      'frame',
      x0,
      0,
      0,
      SLIDING_GATE.post,
      SLIDING_GATE.post,
      h + SLIDING_GATE.postLift,
    );
  });
  const railLen = w + leafW;
  const railRight = w / 2 + SLIDING_GATE.overlap;
  const depth = sections * SLIDING_GATE.leafT + SLIDING_GATE.clear;
  box('ground-rail', 'steel', railRight - railLen, 0, 0, railLen, depth, SLIDING_GATE.groundRailH);
  box('end-stop', 'steel', railRight - railLen - 40 * MM, 0, 0, 40 * MM, depth, SLIDING_GATE.groundRailH * 4);
  box(
    'motor',
    'frame',
    railRight - railLen + 60 * MM,
    depth,
    0,
    SLIDING_GATE.motorW,
    SLIDING_GATE.motorD,
    SLIDING_GATE.motorH,
  );
  box(
    'motor-bracket',
    'steel',
    -w / 2 - SLIDING_GATE.post,
    depth,
    0,
    SLIDING_GATE.motorW * 0.4,
    SLIDING_GATE.groundRailW,
    SLIDING_GATE.groundRailH * 2,
  );
  return out;
}

/**
 * The two-leaf swing gate's parts at one size and state.
 *
 * Each leaf is a framed grid of bars; at an open fraction above zero every part
 * of a leaf is turned about the vertical axis through its hinge edge (the post's
 * inner face) and drawn with that same `yawDeg`, so the leaf stays square to
 * itself as it swings into the property.
 */
export function swingGateParts(params: GateParams): GatePartBox[] {
  const w = Math.max(1, params.widthM);
  const h = Math.max(0.6, params.dropFullM);
  const leaves = gateLeaves(params.prefs.leaves);
  const angleDeg = SWING_GATE.swingMaxDeg * openFraction(params.dropM, params.dropFullM);
  const angle = (angleDeg * Math.PI) / 180;
  const out: GatePartBox[] = [];

  /**
   * One module box → one device-frame part. The module's own `_box(cx, cy, …)`
   * places a box by its CENTRE in plan, so `cy` is a centre here too (the sliding
   * gate's helper takes edges, because THAT module's `_box` does).
   */
  const box = (
    key: string,
    material: GatePartMaterial,
    cx: number,
    cy: number,
    z0: number,
    dx: number,
    dy: number,
    dz: number,
    yawDeg = 0,
  ): void => {
    if (dx <= 0 || dy <= 0 || dz <= 0) return;
    out.push({
      key,
      material,
      center: [cx, z0 + dz / 2 - h / 2, -SWING_GATE.pinStandoff - cy],
      size: [dx, dz, dy],
      ...(yawDeg === 0 ? {} : { yawDeg }),
    });
  };

  /** A leaf's part at its CLOSED place, turned about the hinge (hingeX, 0). */
  const leafBox = (
    key: string,
    material: GatePartMaterial,
    hingeX: number,
    sign: number,
    x0: number,
    z0: number,
    dx: number,
    dz: number,
    dy: number = SWING_GATE.leafT,
  ): void => {
    const cx = x0 + dx / 2;
    const a = sign * angle;
    const rx = hingeX + (cx - hingeX) * Math.cos(a);
    const ry = (cx - hingeX) * Math.sin(a);
    box(key, material, rx, ry, z0, dx, dy, dz, (a * 180) / Math.PI);
  };

  const leafW = leaves === 2 ? (w - SWING_GATE.midGap) / 2 : w;
  for (let i = 0; i < leaves; i += 1) {
    const sign = i === 0 ? 1 : -1;
    const hingeX = i === 0 ? -w / 2 : w / 2;
    const xLeft = i === 0 ? -w / 2 : SWING_GATE.midGap / 2;
    const midZ = SWING_GATE.midRailZ * h;

    [xLeft, xLeft + leafW - SWING_GATE.stileW].forEach((stileX, s) => {
      leafBox(`leaf-${i}-stile-${s}`, 'leaf', hingeX, sign, stileX, 0, SWING_GATE.stileW, h);
    });
    leafBox(`leaf-${i}-rail-bottom`, 'leaf', hingeX, sign, xLeft, 0, leafW, SWING_GATE.railBotH);
    leafBox(
      `leaf-${i}-rail-mid`,
      'leaf',
      hingeX,
      sign,
      xLeft,
      midZ - SWING_GATE.railH / 2,
      leafW,
      SWING_GATE.railH,
    );
    leafBox(`leaf-${i}-rail-top`, 'leaf', hingeX, sign, xLeft, h - SWING_GATE.railH, leafW, SWING_GATE.railH);

    const usable = leafW - 2 * SWING_GATE.stileW;
    const bars = Math.max(2, Math.round((usable - 2 * (SWING_GATE.barPitch - SWING_GATE.bar)) / SWING_GATE.barPitch) + 1);
    const pitch = bars > 1 ? (usable - SWING_GATE.bar) / (bars - 1) : 0;
    for (let k = 0; k < bars; k += 1) {
      leafBox(
        `leaf-${i}-bar-${k}`,
        'leaf',
        hingeX,
        sign,
        xLeft + SWING_GATE.stileW + k * pitch,
        SWING_GATE.railBotH,
        SWING_GATE.bar,
        h - SWING_GATE.railBotH - SWING_GATE.railH,
      );
    }
    // the leaf's halves of its two hinges
    [SWING_GATE.hingeBotLift, h - SWING_GATE.hingeTopDrop].forEach((z0, hi) => {
      const x0 = i === 0 ? hingeX : hingeX - SWING_GATE.hingeW;
      leafBox(`leaf-${i}-hinge-${hi}`, 'steel', hingeX, sign, x0, z0, SWING_GATE.hingeW, SWING_GATE.hingeH);
    });
  }

  // ── the fixed frame: two posts and their hinge plates ────────────────────
  [-1, 1].forEach((sx) => {
    const x0 = sx < 0 ? -w / 2 - SWING_GATE.post : w / 2;
    box(
      sx < 0 ? 'post-l' : 'post-r',
      'frame',
      x0 + SWING_GATE.post / 2,
      SWING_GATE.post / 2,
      0,
      SWING_GATE.post,
      SWING_GATE.post,
      h + SWING_GATE.postLift,
    );
    [SWING_GATE.hingeBotLift, h - SWING_GATE.hingeTopDrop].forEach((z0, hi) => {
      const hx = sx < 0 ? x0 + SWING_GATE.post : x0;
      box(
        sx < 0 ? `post-l-hinge-${hi}` : `post-r-hinge-${hi}`,
        'steel',
        hx,
        SWING_GATE.hingeD / 2,
        z0,
        SWING_GATE.hingeH,
        SWING_GATE.hingeD,
        SWING_GATE.hingeW,
      );
    });
  });

  // ── the latch at the meeting stiles, and the actuator ────────────────────
  box('latch', 'hardware', -SWING_GATE.midGap / 2 - 30 * MM, SWING_GATE.leafT / 2, h * 0.45, 60 * MM, SWING_GATE.leafT * 0.6, 120 * MM);
  box(
    'latch-handle',
    'hardware',
    -SWING_GATE.midGap / 2 - 40 * MM,
    -SWING_GATE.leafT * 0.5,
    h * 0.45 - 220 * MM,
    80 * MM,
    40 * MM,
    40 * MM,
  );
  box(
    'actuator',
    'frame',
    -w / 2 - SWING_GATE.post - SWING_GATE.controllerW / 2 + SWING_GATE.post / 2,
    SWING_GATE.leafT + SWING_GATE.controllerD / 2,
    200 * MM,
    SWING_GATE.controllerW,
    SWING_GATE.controllerD,
    SWING_GATE.controllerH,
  );
  return out;
}
